import { FC, useEffect, useRef, useState } from 'react';
import { Box, Step, Stepper, Typography } from '@mui/material';
import { NavigateBefore } from '@mui/icons-material';
import 'animate.css';

import Generate from '../../assets/videos/generate.mp4';
import Edit from '../../assets/videos/edit.mp4';
import Input from '../../assets/videos/input.mp4';
import PrimaryButton from 'components/Common/PrimaryButton';
import { setIsProductTourFinished } from 'store/global/globalSlice';
import { useAppDispatch } from 'app/hooks';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

const getSteps = (t: TFunction<'translation', undefined>) => [
    {
        label: t('web_app.product_tour_page.step_1.label'),
        video: Input,
        description: t('web_app.product_tour_page.step_1.description'),
        shortLabel: t('web_app.product_tour_page.step_1.short_label'),
        shortDescription: t('web_app.product_tour_page.step_1.short_description'),
    },
    {
        label: t('web_app.product_tour_page.step_2.label'),
        video: Generate,
        description: t('web_app.product_tour_page.step_2.description'),
        shortLabel: t('web_app.product_tour_page.step_2.short_label'),
        shortDescription: t('web_app.product_tour_page.step_2.short_description'),
    },
    {
        label: t('web_app.product_tour_page.step_3.label'),
        video: Edit,
        description: t('web_app.product_tour_page.step_3.description'),
        shortLabel: t('web_app.product_tour_page.step_3.short_label'),
        shortDescription: t('web_app.product_tour_page.step_3.short_description'),
    },
];

const ProductTour: FC = () => {
    const dispatch = useAppDispatch();
    const [activeStep, setActiveStep] = useState(0);
    const [fadeInClass, setFadeInClass] = useState<string>('');
    const [areStepsViewed, setAreStepsViewed] = useState<boolean>(false);
    const videoRefs = useRef<HTMLVideoElement[] | null[]>([]);
    const [hideVideoBorder, setHideVideoBorder] = useState(true);
    const { t } = useTranslation();

    const steps = getSteps(t);

    useEffect(() => {
        setTimeout(() => {
            setHideVideoBorder(false);
        }, 1500);
    });
    useEffect(() => {
        videoRefs.current = videoRefs.current.slice(0, steps.length);
    });
    useEffect(() => {
        videoRefs.current[activeStep]?.play();
    }, [activeStep]);

    const handleNext = () => {
        applyClassAndRemove();
        if (activeStep === steps.length - 1) {
            setAreStepsViewed(true);
            return;
        }
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleStartNow = () => dispatch(setIsProductTourFinished(true));

    const handlePrevious = () => {
        applyClassAndRemove();
        if (areStepsViewed) {
            setAreStepsViewed(false);
            return;
        }
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const applyClassAndRemove = () => {
        setFadeInClass(' animate__animated animate__fadeIn');
        setTimeout(() => setFadeInClass(''), 500);
    };

    return (
        <Box className={`app-tour ${areStepsViewed ? 'completed' : ''}`}>
            {areStepsViewed ? (
                <Typography
                    variant="h5"
                    color="white"
                    className={fadeInClass}
                    sx={{ width: 515, textAlign: 'center' }}
                >
                    {t('web_app.product_tour_page.title_2')}
                </Typography>
            ) : (
                <Typography variant="h5" color="white">
                    {t('web_app.product_tour_page.title')}
                </Typography>
            )}
            <Stepper
                className={areStepsViewed ? 'completed' : ''}
                connector={null}
                activeStep={activeStep}
            >
                {steps.map((step, index) => {
                    return (
                        <Step
                            className={`${index === activeStep ? 'active' : 'inactive'} item item-${
                                index + 1
                            } ${hideVideoBorder ? 'no-border' : ''}`}
                            key={step.label}
                        >
                            <video
                                ref={(video) => (videoRefs.current[index] = video)}
                                preload="metadata"
                                muted
                                controls
                            >
                                <source src={step.video} type="video/mp4" />
                            </video>
                            {areStepsViewed && (
                                <Box className="footer animate__animated animate__fadeIn animate__delay-0.5s">
                                    <Typography className="mb-12" variant="h5">
                                        {step.shortLabel}
                                    </Typography>
                                    <Typography sx={{ width: 250, margin: 'auto' }} variant="body2">
                                        {step.shortDescription}
                                    </Typography>
                                </Box>
                            )}
                        </Step>
                    );
                })}
            </Stepper>
            <Box className="footer animate__animated animate__fadeIn animate__delay-1s">
                {areStepsViewed ? (
                    <Typography className={`mt-20 mb-12 ${fadeInClass}`} variant="h4">
                        {t('web_app.product_tour_page.all_in_place')}
                    </Typography>
                ) : (
                    <>
                        <Typography className={`mt-20 mb-12 ${fadeInClass}`} variant="h4">
                            {steps[activeStep].label}
                        </Typography>
                        <Typography
                            className={fadeInClass}
                            sx={{ width: 560, textAlign: 'center' }}
                            variant="body1"
                        >
                            {steps[activeStep].description}
                        </Typography>
                    </>
                )}
                <Box sx={{ marginTop: 'auto' }} className="mt-40 flex align-center justify-center">
                    {activeStep > 0 && (
                        <button
                            className="flex align-center"
                            onClick={handlePrevious}
                            style={{ marginRight: 68 }}
                        >
                            <NavigateBefore />
                            {t('web_app.product_tour_page.previous')}
                        </button>
                    )}
                    <PrimaryButton
                        className="filled"
                        text={
                            areStepsViewed
                                ? t('web_app.product_tour_page.lets_go')
                                : t('web_app.product_tour_page.got_it')
                        }
                        onClick={areStepsViewed ? handleStartNow : handleNext}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default ProductTour;
